import ClientOnlyPortal from '@/components/ClientOnlyPortal';
import LoadingSmall from '@/components/LoadingSmall';
import { PageOptionContext, PageOptionDispatchContext } from '@/components/dashboard/context';
import { SPACE_SUBSCRIPTIONS } from '@/constants';
import { useOpenPublishPopupDispatch } from '@/store';
import TokenContext from '@/utils/jwt/tokenContext';
import { ArrowLeft } from '@phosphor-icons/react';
import { SPACE_ACTION } from '@typedream/data';
import { Button, Text } from '@typedream/ui';
import classNames from 'classnames';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PagesOptionMetadata } from './Metadata';
import { PagesOptionSetting } from './Setting';
import { PageMetadataTab, PagesOptionProps } from './types';

/**
 * Options menu for a page in the sidebar page tree.
 * This component using `TokenContext`
 */
export function PagesOption({
  isOpen,
  setOpen,
  enableEmpty = false,
  enableDelete = false,
}: PagesOptionProps) {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [metadataTab, setMetadataTab] = useState<PageMetadataTab>(PageMetadataTab.DEFAULT);
  const [upgradeRequired, setUpgradeRequired] = useState(true);
  const [metadataChanged, setMetadataChanged] = useState(false);
  const [imageChangeStatus, setImageChangeStatus] = useState(false);
  const { subscriptionTokenHandler } = useContext(TokenContext);
  const { space, page, isSubmitting } = useContext(PageOptionContext);
  const { setSettings, onCancel, onSave } = useContext(PageOptionDispatchContext);
  const openPopup = useOpenPublishPopupDispatch();

  const ref = useRef<HTMLDivElement>(null);

  const checkSubscription = async () => {
    const sub = await subscriptionTokenHandler.getSubscription(space?.id as string);
    setUpgradeRequired(!sub.isAllowed(SPACE_ACTION.UpdateMetadata, SPACE_SUBSCRIPTIONS));
  };

  const handleMetadataChange = useCallback((changed) => {
    setMetadataChanged(changed);
  }, []);

  const handleMetadataTab: Dispatch<SetStateAction<PageMetadataTab>> = useCallback(
    (val: PageMetadataTab) => {
      setMetadataTab(val);
      ref.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    []
  );

  const checkMetadataChange = useCallback(() => {
    if (metadataChanged) {
      // TODO handle when metadata changed
      // The latest page options doesn't do anything
    } else {
      setOpen(false);
      setSettings({
        name: null,
        slug: null,
        public: false,
        duplicate: false,
      });
      setMetadataTab(PageMetadataTab.DEFAULT);
    }
  }, [metadataChanged]);

  useEffect(() => {
    if (space?.id) {
      checkSubscription();
    }
  }, [space?.id]);

  const bodyContent = useMemo(() => {
    if (space && page) {
      return metadataTab === PageMetadataTab.DEFAULT ? (
        <PagesOptionSetting
          setOpen={setOpen}
          setMetadataTab={handleMetadataTab}
          upgradeRequired={upgradeRequired}
          enableEmpty={enableEmpty}
          enableDelete={enableDelete}
        />
      ) : (
        <PagesOptionMetadata
          metadataOpen={metadataOpen}
          setMetadataOpen={setMetadataOpen}
          metadataTab={metadataTab}
          upgradeRequired={upgradeRequired}
          onMetadataChange={handleMetadataChange}
          imageChangeStatus={imageChangeStatus}
          setImageChangeStatus={setImageChangeStatus}
        />
      );
    }
    return (
      <LoadingSmall
        loading
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      />
    );
  }, [space, page, metadataOpen, metadataTab, upgradeRequired]);

  const headTitle = useMemo(() => {
    switch (metadataTab) {
      case PageMetadataTab.METADATA:
        return 'Meta data';
      case PageMetadataTab.TWITTER:
        return 'Twitter Card';
      case PageMetadataTab.FACEBOOK:
        return 'Facebook Card';
      default:
        return 'Page';
    }
  }, [metadataTab]);

  return isOpen ? (
    <ClientOnlyPortal>
      <div className="fixed top-0 left-0 bottom-0 right-0 bg-transparent overflow-hidden p-2 !pt-[52px]">
        <div
          className="absolute top-0 bottom-0 left-0 right-0 bg-neutral-700 opacity-20"
          role="none"
          onMouseDown={() => {
            checkMetadataChange();
          }}
        />
        <div
          className={classNames(
            'relative inline-flex flex-col gap-4 translate-x-[264px] bg-background-secondary rounded-xl py-5 h-full transition-all duration-200 ease-out',
            {
              'w-96': metadataTab === PageMetadataTab.DEFAULT,
              'w-[500px]': metadataTab !== PageMetadataTab.DEFAULT,
            }
          )}
        >
          <div className="flex items-center gap-2 pb-4 border-0 border-b border-solid border-border mx-5">
            <button
              type="button"
              className="flex border-0 bg-transparent p-0 focus:outline-0 cursor-pointer"
              onClick={() => {
                if (metadataTab === PageMetadataTab.DEFAULT) {
                  checkMetadataChange();
                } else {
                  handleMetadataTab(PageMetadataTab.DEFAULT);
                }
              }}
            >
              <ArrowLeft size={16} weight="bold" className="text-primary-soft" />
            </button>
            <Text
              as="h1"
              size="md"
              weight="semibold"
              variant="primary-soft"
              className="flex-1 tracking-[-0.01em]"
            >
              {headTitle}
            </Text>
            <Button variant="ghost" isDisabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              isLoading={isSubmitting}
              onClick={() =>
                onSave(metadataTab, async () => {
                  openPopup();
                  setImageChangeStatus(false);
                })
              }
            >
              Save
            </Button>
          </div>
          <div
            ref={ref}
            className="relative flex flex-col flex-1 gap-4 h-full overflow-x-hidden overflow-y-auto"
          >
            {bodyContent}
          </div>
        </div>
      </div>
    </ClientOnlyPortal>
  ) : null;
}
